/**
 * Événements — Showcase des événements organisés par BBr uniquement.
 *
 * (Les demandes d'événements privés — mariages, séminaires — sont gérées
 *  depuis l'univers Connexion / Salles.)
 *
 * Alimenté dynamiquement par le Dashboard via /api/special-events
 * (collection MongoDB `special_events`).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { ArrowRight, Calendar as CalIcon, Clock } from "lucide-react";
import api from "../../lib/api";
import { trackEvent } from "../../lib/tracking";
import { useSiteConfig, sel } from "../../lib/site-config";

const fmtXOF = (n) => new Intl.NumberFormat("fr-FR").format(n || 0) + " FCFA";

function formatEventDates(ev) {
  // Multi-day: display start_date → end_date range if present
  if (ev.event_kind === "multi_day" && ev.start_date && ev.end_date) {
    try {
      const s = format(parseISO(ev.start_date), "d MMM", { locale: frLocale });
      const e = format(parseISO(ev.end_date), "d MMM yyyy", { locale: frLocale });
      return `${s} — ${e}`;
    } catch { /* fall through */ }
  }
  // Single-day mode: show the first date (or all if <=2)
  const dates = ev.event_dates || [];
  if (dates.length === 0) return "";
  if (dates.length === 1) {
    try { return format(parseISO(dates[0]), "EEEE d MMMM yyyy", { locale: frLocale }); } catch { return dates[0]; }
  }
  if (dates.length <= 3) {
    return dates.map((d) => {
      try { return format(parseISO(d), "d MMM", { locale: frLocale }); } catch { return d; }
    }).join(" · ");
  }
  return `${dates.length} dates programmées`;
}

export default function UniversEvenementiel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/special-events")
      .then((r) => setEvents(r.data?.items || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const gridCols = useMemo(() => {
    if (events.length === 1) return "md:grid-cols-1 max-w-xl mx-auto";
    if (events.length === 2) return "md:grid-cols-2";
    return "md:grid-cols-2 lg:grid-cols-3";
  }, [events.length]);

  const cfg = useSiteConfig();
  const ph = sel.pageHeader(cfg, "hero_events");
  const heroKicker = ph.kicker || "· Beach Life · Programmation ·";
  const heroTitle = ph.title || "Nos événements";
  const heroSubtitle = ph.subtitle_long || "Découvrez toute la programmation de BBr et réservez votre place pour nos événements exclusifs — sunset parties, showcases, pool parties, DJ guests, soirées à thème et festivals signature.";
  const emptyKicker = ph.empty_kicker || "· Bientôt ·";
  const emptyBody = ph.empty_body || "Notre prochaine programmation arrive très bientôt. Inscrivez-vous à notre newsletter pour être les premiers informés.";

  return (
    <div className="bg-white text-[#0A0A0A]" data-testid="univers-evenements">
      {/* ─── Sober introduction (CMS iter-63) ─── */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-white border-b border-[#0A0A0A]/8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-[0.62rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
            {heroKicker}
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#0A0A0A] whitespace-pre-line">
            {heroTitle}
          </h1>
          <p className="mt-6 md:mt-8 max-w-2xl mx-auto text-base md:text-lg text-[#0A0A0A]/70 font-light leading-relaxed">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* ─── Events grid — dynamic, driven by staff dashboard ─── */}
      <section className="py-20 md:py-28 bg-[#FAF7F2]" data-testid="events-grid-section">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="text-center text-sm text-[#0A0A0A]/45 py-16">Chargement de la programmation…</div>
          ) : events.length === 0 ? (
            <div className="max-w-lg mx-auto text-center py-16">
              <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45 mb-3">
                {emptyKicker}
              </div>
              <p className="text-base text-[#0A0A0A]/65 font-light">
                {emptyBody}
              </p>
            </div>
          ) : (
            <div className={`grid gap-8 ${gridCols}`} data-testid="events-grid">
              {events.map((ev) => (
                <EventCard key={ev.id} ev={ev} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EventCard({ ev }) {
  const dateLabel = formatEventDates(ev);
  const bookUrl = `/event/${ev.id}`;
  return (
    <article
      className="group flex flex-col bg-white shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] hover:shadow-[0_30px_70px_-20px_rgba(0,0,0,0.28)] transition-shadow duration-500 overflow-hidden"
      data-testid={`event-card-${(ev.id || "").slice(0, 8)}`}
    >
      <Link to={bookUrl} onClick={() => trackEvent("event_card_click", { id: ev.id })} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F0E9]">
          {ev.image_url ? (
            <img
              src={ev.image_url}
              alt={ev.title}
              className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#0A0A0A]/25 text-xs tracking-widest uppercase">
              Photo à venir
            </div>
          )}
          {dateLabel && (
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 text-[0.6rem] tracking-[0.3em] uppercase text-[#0A0A0A] flex items-center gap-1.5">
              <CalIcon size={11} strokeWidth={1.5} />
              {dateLabel}
            </div>
          )}
        </div>
      </Link>

      <div className="p-7 md:p-8 flex flex-col flex-1">
        {ev.subtitle && (
          <div className="text-[0.58rem] tracking-[0.4em] uppercase text-[#B8922A] mb-3">
            {ev.subtitle}
          </div>
        )}
        <h3 className="font-serif font-light text-2xl md:text-3xl leading-tight text-[#0A0A0A] mb-3">
          {ev.title}
        </h3>
        {ev.description && (
          <p className="text-sm text-[#0A0A0A]/65 font-light leading-relaxed mb-5 line-clamp-3">
            {ev.description}
          </p>
        )}

        {Array.isArray(ev.boat_times) && ev.boat_times.length > 0 && (
          <div className="flex items-center gap-1.5 text-[0.65rem] tracking-[0.2em] uppercase text-[#0A0A0A]/55 mb-4">
            <Clock size={11} strokeWidth={1.5} />
            {ev.boat_times.slice(0, 3).join(" · ")}
            {ev.boat_times.length > 3 && ` +${ev.boat_times.length - 3}`}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between gap-4 pt-5 border-t border-[#0A0A0A]/10">
          <div>
            <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45">À partir de</div>
            <div className="font-serif text-2xl text-[#0A0A0A] mt-0.5">
              {ev.price_adult ? fmtXOF(ev.price_adult) : "Sur invitation"}
            </div>
          </div>
          <Link
            to={bookUrl}
            onClick={() => trackEvent("event_book_click", { id: ev.id })}
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#B8922A] text-white px-6 py-3 text-[0.68rem] tracking-[0.32em] uppercase transition-colors duration-300"
            data-testid={`event-book-${(ev.id || "").slice(0, 8)}`}
          >
            {ev.cta_label || "Réserver"}
            <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </article>
  );
}
